import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { IntlProvider } from 'react-intl';

import { ApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { EPage, ReactFCC } from '@types';
import { noop } from '@utils/general';

import { LocalizationReducer } from './localizationReducer/model';
import { defaultReducerValue, reducer } from './localizationReducer/reducer';
import { useActions } from './localizationReducer/useActions';

interface ILocalizationContext {
  translationsReady: boolean;
  triggerTranslationLoad: (page: EPage) => void;
}

const defaultValue: ILocalizationContext = {
  translationsReady: false,
  triggerTranslationLoad: noop,
};

export const LocalizationContext = createContext<ILocalizationContext>(defaultValue);

export const LocalizationContextProvider: ReactFCC = ({ children }) => {
  const {
    applicationSettings: {
      localization: { languageIdentifier },
    },
  } = useContext(ApplicationRuntimeContext);

  const [state, dispatch] = useReducer<LocalizationReducer>(reducer, defaultReducerValue);
  const { triggerTranslationLoad } = useActions(dispatch);

  const value: ILocalizationContext = useMemo(
    () => ({ translationsReady: state.translationsReady, triggerTranslationLoad }),
    [state.translationsReady, triggerTranslationLoad],
  );

  return (
    <LocalizationContext.Provider value={value}>
      <IntlProvider locale={languageIdentifier || 'en-EN'} messages={state.translations}>
        {children}
      </IntlProvider>
    </LocalizationContext.Provider>
  );
};

export const useLocalizationContext = () => useContext(LocalizationContext);
